// F-N batch exhibit upload dialog (exhibition form → "Batch upload
// exhibits"). One action (2a full-auto): "Upload & import" validates
// server-side and, when clean, imports immediately; when dirty it shows
// the verbatim report and offers the marked-up workbook (failing cells
// red + an errors column — fix it and drop the SAME file again, 2c).
// Files arrive via a drag-drop zone (or click-to-browse): .xlsx/.zip
// becomes THE workbook (replaced), everything else accumulates as media
// (additive across drops, deduped by name, per-file remove).

import {
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type FC,
} from "react";
import { toast } from "sonner";

import { Button } from "@shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";
import { extractErrorMessage } from "@shared/api/errors";
import {
  downloadAnnotatedWorkbook,
  downloadBatchTemplate,
  type BatchImportResult,
  type BatchValidationReport,
} from "@shared/api/exhibitions";
import {
  useBatchImportExhibits,
  useBatchValidateExhibits,
} from "@shared/api/hooks/useExhibitions";

export interface BatchUploadDialogProps {
  exhibitionId: string;
  exhibitionCode: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// The generated wire types mark server-defaulted fields optional;
// normalize once at the state boundary so the JSX reads plainly.
function normReport(r: BatchValidationReport) {
  return {
    ok: r.ok ?? false,
    hasWarnings: r.hasWarnings ?? false,
    totalValid: r.totalValid ?? 0,
    unmatchedTabs: r.unmatchedTabs ?? [],
    unusedFiles: r.unusedFiles ?? [],
    tabs: (r.tabs ?? []).map((t) => ({
      zoneId: t.zoneId,
      zoneLabel: t.zoneLabel,
      validCount: t.validCount,
      tabErrors: t.tabErrors ?? [],
      rowErrors: t.rowErrors ?? [],
      rowWarnings: t.rowWarnings ?? [],
    })),
  };
}
type NormalizedReport = ReturnType<typeof normReport>;

function normResult(r: BatchImportResult) {
  return {
    total: r.total ?? 0,
    createdByZone: (r.createdByZone ?? []).map((z) => ({
      zoneId: z.zoneId,
      count: z.count,
      exhibitIds: z.exhibitIds ?? [],
    })),
  };
}
type NormalizedResult = ReturnType<typeof normResult>;

const isWorkbookName = (name: string) => /\.(xlsx|zip)$/i.test(name);

const BatchUploadDialog: FC<BatchUploadDialogProps> = ({
  exhibitionId,
  exhibitionCode,
  open,
  onOpenChange,
}) => {
  const [workbook, setWorkbook] = useState<File | null>(null);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [report, setReport] = useState<NormalizedReport | null>(null);
  const [result, setResult] = useState<NormalizedResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const browseRef = useRef<HTMLInputElement | null>(null);

  const validateMut = useBatchValidateExhibits();
  const importMut = useBatchImportExhibits();
  const busy = validateMut.isPending || importMut.isPending;

  const reset = () => {
    setWorkbook(null);
    setMediaFiles([]);
    setReport(null);
    setResult(null);
    setDragOver(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleTemplate = async () => {
    try {
      await downloadBatchTemplate(exhibitionId, exhibitionCode);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Template download failed"));
    }
  };

  // Workbook (.xlsx/.zip) is replaced by the newest one; media files
  // ACCUMULATE across drops/picks (deduped by name, newest wins).
  const addFiles = (incoming: File[]) => {
    setReport(null);
    setResult(null);
    const wb = incoming.filter((f) => isWorkbookName(f.name)).pop();
    if (wb) setWorkbook(wb);
    const media = incoming.filter((f) => !isWorkbookName(f.name));
    if (media.length > 0) {
      setMediaFiles((prev) => {
        const names = new Set(media.map((f) => f.name));
        return [...prev.filter((f) => !names.has(f.name)), ...media];
      });
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleBrowse = (e: ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    e.target.value = ""; // allow re-picking the same file after a fix
  };

  const removeMedia = (name: string) =>
    setMediaFiles((prev) => prev.filter((f) => f.name !== name));

  const runImport = async (rep: NormalizedReport, allowDuplicates: boolean) => {
    if (!workbook) return;
    const res = normResult(
      await importMut.mutateAsync({
        exhibitionId,
        workbook,
        files: mediaFiles,
        allowDuplicates,
      }),
    );
    setReport(rep);
    setResult(res);
    toast.success(
      `Created ${res.total} exhibits as Draft across ${res.createdByZone.length} zone(s)`,
    );
  };

  // 2a full-auto: validate, then import immediately when clean. Duplicate
  // titles are the one confirm-tier case: the panel below asks first, and
  // the server independently refuses unconfirmed duplicates.
  const handleUpload = async () => {
    if (!workbook) return;
    try {
      const rep = normReport(
        await validateMut.mutateAsync({
          exhibitionId,
          workbook,
          files: mediaFiles,
        }),
      );
      if (!rep.ok) {
        setReport(rep);
        toast.error("Validation failed — see the report below");
        return;
      }
      if (rep.hasWarnings) {
        setReport(rep); // pause: render the duplicate-confirm panel
        return;
      }
      await runImport(rep, false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Upload failed"));
    }
  };

  const handleConfirmDuplicates = async () => {
    if (!report) return;
    try {
      await runImport(report, true);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Upload failed"));
    }
  };

  const handleDownloadMarkup = async () => {
    if (!workbook) return;
    try {
      await downloadAnnotatedWorkbook(exhibitionId, workbook, mediaFiles);
    } catch (err) {
      toast.error(
        extractErrorMessage(err, "Could not build the marked-up workbook"),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="batch-upload-dialog"
      >
        <DialogHeader>
          <DialogTitle>Batch upload exhibits</DialogTitle>
          <DialogDescription>
            One workbook per exhibition; each tab is one zone. Download the
            template, fill one row per exhibit, then drop the workbook plus the
            referenced media files (or one .zip of everything).
          </DialogDescription>
        </DialogHeader>

        {result === null ? (
          <div className="space-y-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleTemplate}
              data-testid="batch-template-button"
            >
              Download template
            </Button>

            <div
              role="button"
              tabIndex={0}
              onClick={() => browseRef.current?.click()}
              onKeyDown={(e) => e.key === "Enter" && browseRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`rounded-lg border-2 border-dashed p-6 text-center text-sm cursor-pointer transition-colors ${
                dragOver
                  ? "border-primary bg-primary/5"
                  : "border-border text-muted-foreground hover:border-primary/50"
              }`}
              data-testid="batch-drop-zone"
            >
              Drag &amp; drop the filled .xlsx and its media files here (more
              drops add media) — or click to browse
              <input
                ref={browseRef}
                type="file"
                multiple
                hidden
                onChange={handleBrowse}
                data-testid="batch-browse-input"
              />
            </div>

            {(workbook || mediaFiles.length > 0) && (
              <div className="text-sm space-y-1" data-testid="batch-file-list">
                <p>
                  Workbook:{" "}
                  {workbook ? (
                    <strong>{workbook.name}</strong>
                  ) : (
                    <span className="text-destructive">
                      none yet — drop the .xlsx
                    </span>
                  )}
                </p>
                {mediaFiles.length > 0 && (
                  <ul className="flex flex-wrap gap-2">
                    {mediaFiles.map((f) => (
                      <li
                        key={f.name}
                        className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs"
                      >
                        {f.name}
                        <button
                          type="button"
                          aria-label={`Remove ${f.name}`}
                          onClick={() => removeMedia(f.name)}
                          className="text-muted-foreground hover:text-destructive"
                          data-testid={`batch-remove-${f.name}`}
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {report !== null && report.ok && report.hasWarnings && (
              <div
                className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm space-y-2"
                data-testid="batch-duplicate-panel"
              >
                <p className="font-medium text-amber-900">
                  Some rows duplicate existing exhibit titles — create them
                  anyway?
                </p>
                <div className="space-y-1 text-amber-800">
                  {report.tabs.flatMap((tab) =>
                    tab.rowWarnings.map((rw) => (
                      <p key={`${tab.zoneId}-${rw.row}`}>
                        Row {rw.row} ({tab.zoneLabel}): {rw.warnings.join("; ")}
                      </p>
                    )),
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    disabled={busy}
                    onClick={handleConfirmDuplicates}
                    data-testid="batch-confirm-duplicates-button"
                  >
                    Create anyway
                  </Button>
                </div>
                <p className="text-xs text-amber-700">
                  Or remove those rows from the sheet and drop it here again.
                </p>
              </div>
            )}

            {report !== null && !report.ok && (
              <div
                className="rounded-lg border border-border p-3 text-sm space-y-2"
                data-testid="batch-report"
              >
                <div className="space-y-1" data-testid="batch-report-errors">
                  {report.unmatchedTabs.map((t) => (
                    <p key={t} className="text-destructive">
                      Tab "{t}" matches no zone in this exhibition
                    </p>
                  ))}
                  {report.tabs.map((tab) => (
                    <div key={tab.zoneId}>
                      {tab.tabErrors.map((e) => (
                        <p key={e} className="text-destructive">
                          {tab.zoneLabel}: {e}
                        </p>
                      ))}
                      {tab.rowErrors.map((re) => (
                        <p
                          key={`${tab.zoneId}-${re.row}`}
                          className="text-destructive"
                        >
                          Row {re.row} ({tab.zoneLabel}): {re.errors.join("; ")}
                        </p>
                      ))}
                    </div>
                  ))}
                </div>
                {report.unusedFiles.length > 0 && (
                  <p
                    className="text-destructive"
                    data-testid="batch-unused-files"
                  >
                    Attached but never referenced:{" "}
                    {report.unusedFiles.join(", ")} — remove the file(s) with ✕
                    above, or reference them in the sheet. Attachments and sheet
                    references must match exactly.
                  </p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDownloadMarkup}
                  data-testid="batch-download-markup-button"
                >
                  Download marked-up workbook
                </Button>
                <p className="text-xs text-muted-foreground">
                  Failing cells are highlighted with the reason in the last
                  column — fix them in that file and drop it here again.
                </p>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                disabled={workbook === null || busy}
                onClick={handleUpload}
                data-testid="batch-upload-go-button"
              >
                {validateMut.isPending
                  ? "Validating…"
                  : importMut.isPending
                    ? "Importing…"
                    : "Upload & import"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4" data-testid="batch-result">
            <p className="text-sm">
              Created <strong>{result.total}</strong> exhibits as Draft:
            </p>
            <ul className="text-sm list-disc pl-5">
              {result.createdByZone.map((z) => (
                <li key={z.zoneId}>
                  {z.count} in zone{" "}
                  {report?.tabs.find((t) => t.zoneId === z.zoneId)?.zoneLabel ??
                    z.zoneId}
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              Review and publish them from each zone's exhibit list.
            </p>
            <DialogFooter>
              <Button
                type="button"
                onClick={() => handleOpenChange(false)}
                data-testid="batch-done-button"
              >
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BatchUploadDialog;
